import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { ArrowRight, Check, Lock } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function WaitlistCTA() {
  const { t, locale } = useLanguage();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [channel, setChannel] = useState("");
  const [toolsCount, setToolsCount] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const channels = t("waitlist.channels");
  const toolsOptions = t("waitlist.tools_options");
  const validate = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const submit = async (e) => {
    e.preventDefault();
    if (!validate(email)) {
      toast.error(t("waitlist.error"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, {
        email,
        first_name: firstName,
        channel,
        tools_count: toolsCount,
        locale,
      });
      setDone(true);
      toast.success(t("waitlist.success_title"));
    } catch (err) {
      toast.error(
        err.response?.status === 422 ? t("waitlist.error") : t("waitlist.error_server")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="waitlist"
      data-testid="waitlist-section"
      className="relative overflow-hidden bg-[#1A1814] text-white"
    >
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[700px] opacity-35"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(67,125,234,0.45) 0%, rgba(143,159,254,0.15) 40%, transparent 70%)",
            filter: "blur(50px)",
          }}
        />
      </div>

      <div className="container-iko relative py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left — copy */}
          <div className="lg:col-span-6">
            <div data-testid="waitlist-eyebrow">
              <SectionLabel icon={Lock} onDark>
                {t("waitlist.eyebrow")}
              </SectionLabel>
            </div>
            <h2 className="font-display font-semibold text-4xl lg:text-[58px] leading-[1] tracking-[-0.034em] text-balance mb-6">
              {t("waitlist.title")}
            </h2>
            <p className="text-lg text-white/70 leading-relaxed max-w-md mb-9">
              {t("waitlist.subtitle")}
            </p>

            <div className="border-t border-white/10 pt-7 max-w-md">
              <p className="font-display font-semibold text-xl lg:text-2xl leading-[1.15] tracking-[-0.02em] text-balance mb-3">
                <span className="block text-white">{t("waitlist.side_title_l1")}</span>
                <span className="block text-white/55">{t("waitlist.side_title_l2")}</span>
              </p>
              <p className="text-[14px] text-white/55 leading-relaxed">
                {t("waitlist.side_body")}
              </p>
            </div>
          </div>

          {/* Right — form */}
          <div className="lg:col-span-6">
            {done ? (
              <div
                data-testid="waitlist-success"
                className="relative overflow-hidden rounded-[28px] bg-[#FAFAF7] text-[#1A1814] p-8 lg:p-10 border border-[#437DEA]/30 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.35)]"
              >
                <div className="absolute -top-12 -right-12 w-52 h-52 halo-soft opacity-70" />
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#437DEA] to-[#8F9FFE] text-white flex items-center justify-center mb-5">
                    <Check size={20} strokeWidth={2.5} />
                  </div>
                  <h3 className="font-display font-bold text-2xl tracking-[-0.02em] mb-2">
                    {t("waitlist.success_title")}
                  </h3>
                  <p className="text-[15px] text-[#6B6862] mb-4">
                    {t("waitlist.success_body")}
                  </p>
                  <p className="font-mono text-xs text-[#6B6862] tracking-wide">{email}</p>
                </div>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="relative overflow-hidden rounded-[28px] bg-[#FAFAF7] p-7 lg:p-8 text-[#1A1814] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.45)]"
                data-testid="waitlist-form"
              >
                <div className="absolute -top-16 -right-12 w-56 h-56 halo-soft opacity-50 pointer-events-none" />

                <div className="relative space-y-3.5">
                  <input
                    data-testid="waitlist-email-input"
                    type="email"
                    required
                    placeholder={t("waitlist.placeholder_email")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3.5 rounded-2xl bg-white border border-[#F0EBE0] focus:border-[#437DEA] outline-none text-[15px] placeholder:text-[#94918A] transition-colors"
                  />
                  <input
                    data-testid="waitlist-first-name-input"
                    type="text"
                    placeholder={t("waitlist.placeholder_first_name")}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-3.5 rounded-2xl bg-white border border-[#F0EBE0] focus:border-[#437DEA] outline-none text-[15px] placeholder:text-[#94918A] transition-colors"
                  />

                  <div className="pt-1">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B6862] mb-3">
                      {t("waitlist.channel_label")}
                    </div>
                    <div className="flex flex-wrap gap-1" data-testid="waitlist-channel-group">
                      {channels.map((c) => {
                        const selected = channel === c;
                        return (
                          <button
                            type="button"
                            key={c}
                            data-testid={`waitlist-channel-${c.toLowerCase()}`}
                            onClick={() => setChannel(selected ? "" : c)}
                            className={`px-4 py-2 text-[13px] font-medium border-b transition-colors ${
                              selected
                                ? "border-[#1A1814] text-[#1A1814]"
                                : "border-transparent text-[#6B6862] hover:text-[#1A1814] hover:border-[#ECE8DD]"
                            }`}
                          >
                            {c}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="pt-1">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B6862] mb-3">
                      {t("waitlist.tools_label")}
                    </div>
                    <div className="flex flex-wrap gap-1" data-testid="waitlist-tools-group">
                      {toolsOptions.map((o) => {
                        const selected = toolsCount === o;
                        return (
                          <button
                            type="button"
                            key={o}
                            data-testid={`waitlist-tools-${o}`}
                            onClick={() => setToolsCount(selected ? "" : o)}
                            className={`px-4 py-2 text-[13px] font-medium border-b transition-colors ${
                              selected
                                ? "border-[#1A1814] text-[#1A1814]"
                                : "border-transparent text-[#6B6862] hover:text-[#1A1814] hover:border-[#ECE8DD]"
                            }`}
                          >
                            {o}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <button
                    type="submit"
                    data-testid="waitlist-submit-button"
                    disabled={loading}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#1A1814] text-white font-medium text-[14px] hover:bg-[#437DEA] transition-colors disabled:opacity-50 mt-2"
                  >
                    {loading ? "…" : t("waitlist.cta")} <ArrowRight size={16} strokeWidth={2} />
                  </button>
                  <p className="font-mono text-[10px] tracking-[0.18em] text-[#6B6862] text-center pt-1">
                    {t("waitlist.microcopy")}
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
